import { Theme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  ContentTemplate,
  Grid,
  HeaderTemplate,
  KeyValue,
  Progress,
  SectionHeader,
  Stepper,
  TitleHeader,
  useSnackbarHandler,
} from 'web-library';
import TransactionAuditLogUpdatedDetails from 'web-library/components/TransactionAuditLogUpdatedDetails';
import { AUTHORITY_NAMES } from 'web-library/models/AuthorityFunctions';
import { useGetNonNationalApplicationStatus } from '../../../api-refactored/non-national-exam-registration/useGetNonNationalApplicationStatus';
import { useGetNonNationalApplicationWithdrawalStatus } from '../../../api-refactored/non-national-exam-registration/useGetNonNationalApplicationWithdrawalStatus';
import { useGetNonNationalExamWithdrawalSummary } from '../../../api-refactored/non-national-exam-registration/useGetNonNationalExamWithdrawalSummary';
import { usePutNonNationalWithdrawalApplicationStatus } from '../../../api-refactored/non-national-exam-registration/usePutNonNationalWithdrawalApplicationStatus';
import { useGetUserAuthorities } from '../../../api-refactored/user/useGetUserAuthorities';
import ViewAuditLog from '../../../modules/examRegistration/components/nonNationalExam/ViewAuditLog';
import ApplicationReviewForm from '../../../modules/withdrawal/components/admin/ApplicationReviewForm';
import ReviewWithdrawalCandidateParticulars from '../../../modules/withdrawal/ReviewWithdrawalCandidateParticulars';
import ReviewWithdrawalDetails from '../../../modules/withdrawal/ReviewWithdrawalDetails';
import { existInArray } from '../../../utils/ArrayUtils';
import { stringToLongDateAndShortTime } from '../../../utils/DateUtils';
import ReviewWithdrawalExamDetails from './ReviewWithdrawal/ReviewWithdrawalExamDetails';

const useStyles = makeStyles()((theme) =>
({
  stepper: {
    position: 'relative',
    [theme.breakpoints.up('md')]: {
      left: '-15%',
    },
    [theme.breakpoints.between('sm', 'lg')]: {
      left: '-14%',
    },
    [theme.breakpoints.between('xs', 'md')]: {
      left: '-13%',
    },
    [theme.breakpoints.down('sm')]: {
      left: '-6%',
    },
  },
  personalDetailsContainer: {
    marginBottom: theme.spacing(4),
  },
}));

interface WithdrawalSummaryOpsAdminProps {
  routeSteps: { label: string }[];
}

const WithdrawalSummaryOpsAdmin = ({ routeSteps }: WithdrawalSummaryOpsAdminProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();
  const { snackbarHandler } = useSnackbarHandler();
  const [isLoadingLastUpdated, setIsLoadingLastUpdated] = useState<boolean>(true);
  const [isOpenAuditDialog, setIsOpenAuditDialog] = useState<boolean>(false);

  const authorities = useGetUserAuthorities();
  const hasEditAccess = existInArray(
    authorities.data,
    AUTHORITY_NAMES.CP_ADMIN_NON_NATIONAL_APPLICATIONS_REVIEW_EDIT
  );
  const { data: withdrawalDetails } = useGetNonNationalExamWithdrawalSummary({ uuid });

  const {
    data: applicationStatusData,
    refetch: refetchApplicationStatus,
  } = useGetNonNationalApplicationStatus({
    uuid,
    onSuccess: (_) => {
      setIsLoadingLastUpdated(false);
    },
  });

  const { data: applicationStatusDropdown } = useGetNonNationalApplicationWithdrawalStatus({});

  const putNonNationalExamApplicationStatus = usePutNonNationalWithdrawalApplicationStatus({
    uuid,
    onSuccess: () => {
      if (!isLoadingLastUpdated) {
        setIsLoadingLastUpdated(true);
        refetchApplicationStatus();
      }
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('exam->>NonNationalExam.ApplicationSuccessfullyUpdated'),
      });
    },
  });

  const handleOpenAuditLog = () => {
    setIsOpenAuditDialog(true);
  };

  const handleCloseAuditLog = () => {
    setIsOpenAuditDialog(false);
  };

  const isFinanceGroup = () =>
    applicationStatusDropdown?.some((d) => d.description === 'Withdrawn (Refunded)');

  const isStatusUpdatable = () => {
    if (applicationStatusData?.status === 'WITHDRAWN_PENDING_MOE') {
      return true;
    }
    return applicationStatusData?.status === 'WITHDRAWN_REFUND_APPROVED' && isFinanceGroup();
  };

  if (
    !withdrawalDetails ||
    !applicationStatusData ||
    !applicationStatusDropdown ||
    !putNonNationalExamApplicationStatus
  ) {
    return (
      <ContentTemplate transparent continuous>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <TitleHeader title={t('withdrawal->>ViewApplication.TitleHeader')} />
      <HeaderTemplate>
        {isStatusUpdatable() && (
          <Stepper className={classes.stepper} activeStep={1} steps={routeSteps} />
        )}
      </HeaderTemplate>
      <ContentTemplate isDividerBottom type="large" isContinuable>
        <Grid className={classes.personalDetailsContainer}>
          <Grid container direction="row" alignItems="flex-start">
            <SectionHeader title={t('withdrawal->>ViewApplication.ApplicationDetails')} />
          </Grid>
          <Grid>
            <KeyValue
              id="applicationId"
              keyText={t('withdrawal->>ViewApplication.ApplicationId')}
              valueText={withdrawalDetails.applicationId}
            />
          </Grid>
          <Grid>
            <KeyValue
              id="applicationDateTime"
              keyText={t('withdrawal->>ViewApplication.ApplicationDateTime')}
              valueText={stringToLongDateAndShortTime(withdrawalDetails.applicationDateTime)}
            />
          </Grid>
          <Grid>
            <KeyValue
              id="lastUpdatedDateTime"
              keyText={t('withdrawal->>ViewApplication.LastUpdatedDateTime')}
              valueText={stringToLongDateAndShortTime(withdrawalDetails.lastUpdatedDateTime)}
            />
          </Grid>
        </Grid>
      </ContentTemplate>
      <ReviewWithdrawalDetails withdrawalDetails={withdrawalDetails} isSectionHeader={false} />
      <ReviewWithdrawalCandidateParticulars withdrawalDetails={withdrawalDetails} />
      <ReviewWithdrawalExamDetails withdrawalDetails={withdrawalDetails} isDividerBottom />
      {isStatusUpdatable() && (
        <ApplicationReviewForm
          formData={{
            putNonNationalExamApplicationStatus,
            applicationStatusDropdown,
            applicationStatusData,
          }}
          handleOpenAuditLog={handleOpenAuditLog}
          isLoadingLastUpdated={isLoadingLastUpdated}
          hasEditAccess={hasEditAccess}
        />
      )}
      {!isStatusUpdatable() && (
        <ContentTemplate isContinuable type="full">
          <TransactionAuditLogUpdatedDetails
            updatedAt={applicationStatusData.adminUpdatedAt}
            updatedBy={applicationStatusData.adminUpdatedByName}
            handleOpenLogModal={handleOpenAuditLog}
            label={t('exam->>NonNationalExam.ViewLog')}
          />
        </ContentTemplate>
      )}

      <ViewAuditLog onClose={handleCloseAuditLog} isOpenDialog={isOpenAuditDialog} />
    </>
  );
};

export default WithdrawalSummaryOpsAdmin;
